import { alpha } from '@material-ui/core';
import { IconButton, Paper, Typography } from '@mui/material';
import _, { isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';
import MedicationIcon from '@mui/icons-material/Medication';
import AgyleAccordion from '../../../../../components/ui/accordion/agyleAccordion';
import { AgyleTable, createAgyleTableColumn } from '../../../../../components/table/AgyleTable';
import * as React from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import useOpenState from '../../../../../hooks/useOpenState';
import MessageBox from '../../../../../util/messageBox';
import moment from 'moment';
import clsx from 'clsx';
import { Box } from '@material-ui/core';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';

const MEDICATIONS_LIMIT = 4;

const PrescriptionsList = ({ medications, disabled, onAction }) => {
    const { t } = useTranslation();

    const [expanded, setExpanded] = useState(true);

    // Confirm delete
    const [isConfirmOpen, openConfirm, closeConfirm] = useOpenState(false, true);
    const tempObj = useRef(null);

    const medicineCellValueExtra = useCallback(
        (column, data) => {
            const vmp = _.get(data, 'vmp');
            if (!vmp) {
                return '-';
            }

            const amp = _.get(data, 'amp');
            const ampp = _.get(data, 'ampp');

            const renderMedicine = (medicine, isMain) => {
                const colorPattern = isMain ? 'primary' : 'success';
                return (
                    <Paper
                        variant="outlined"
                        sx={{
                            width: '100%',
                            p: 0.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            backgroundColor: (theme) => alpha(theme.palette[colorPattern].main, 0.08),
                            borderColor: (theme) => alpha(theme.palette[colorPattern].main, 0.2),
                        }}
                    >
                        <Box>
                            <Typography
                                variant="caption"
                                sx={{
                                    color: `${colorPattern}.main`,
                                    fontWeight: 600,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                }}
                            >
                                {t(isMain ? 'Generic drug' : 'Selected Product')}
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 600,
                                    color: 'text.primary',
                                }}
                            >
                                {medicine.display || '-'}
                            </Typography>
                        </Box>
                    </Paper>
                );
            };

            const res = [];
            res.push(renderMedicine(vmp, true));
            if (amp) {
                res.push(renderMedicine(amp, false));
            }
            if (ampp) {
                res.push(renderMedicine(ampp, false));
            }

            return res;
        },
        [t],
    );

    const COLUMNS = useMemo(
        () => [
            createAgyleTableColumn('no', 'No.', 0, null, 'count', 'notSort', 'medium', 'center', true, false, 40),
            createAgyleTableColumn('vmp', 'Medicine', 1, 'vmp', '$custom', 'notSort', 'medium', 'left', true, false, null, null, [
                medicineCellValueExtra,
            ]),
            createAgyleTableColumn('dose', 'Dose', 2, 'dose.value', 'string', 'notSort', 'medium', 'center', true, false, null, null),
            createAgyleTableColumn('route', 'Route', 3, 'route.value', 'string', 'notSort', 'medium', 'center', true, false, null, null),
            createAgyleTableColumn(
                'frequency',
                'Frequency',
                4,
                'frequency.value',
                'string',
                'notSort',
                'medium',
                'center',
                true,
                false,
                null,
                null,
            ),
            createAgyleTableColumn(
                'duration',
                'Duration',
                5,
                'duration.value',
                'string',
                'notSort',
                'medium',
                'center',
                true,
                false,
                null,
                null,
            ),
            createAgyleTableColumn('action', 'Action', 6, null, 'action', 'notSort', 'medium', 'center', true, true, 100, null),
        ],
        [medicineCellValueExtra],
    );

    const handleEditMedication = (e) => {
        if (onAction) {
            onAction({ action: 'edit', data: e });
        }
    };

    const handlePreviewMedication = (e) => {
        if (onAction) {
            onAction({ action: 'prev', data: e });
        }
    };

    const handleDeleteMedication = (e) => {
        tempObj.current = e;
        openConfirm();
    };

    const handleDeleteMedicineConfirmed = () => {
        if (tempObj.current) {
            if (onAction) {
                onAction({ action: 'delete', data: tempObj.current });
            }

            closeConfirm();
            tempObj.current = null;
        }
    };

    const handleCloseConfirm = () => {
        closeConfirm();
        tempObj.current = null;
    };

    const renderPreviewButton = (obj) => {
        const onSelect = () => {
            console.log('Obj', obj);
            handlePreviewMedication(obj);
        };

        if (disabled) {
            return (
                <IconButton size="small" onClick={onSelect}>
                    <RemoveRedEyeOutlinedIcon />
                </IconButton>
            );
        }

        return null;
    };

    return (
        <>
            <AgyleAccordion
                expanded={expanded}
                onChange={() => setExpanded((prevState) => !prevState)}
                label={t('Medications') + (medications?.length ? ` (${medications.length} of ${MEDICATIONS_LIMIT})` : '')}
                formBackground
            >
                {isEmpty(medications) && (
                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            textAlign: 'center',
                            borderRadius: 2,
                            // border: '1px dashed',
                            borderColor: 'divider',
                            backgroundColor: 'transparent',
                        }}
                    >
                        <MedicationIcon style={{ fontSize: 48 }} />
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                            {t('No medications added yet')}
                        </Typography>
                        <Typography variant="body2" color="text.disabled">
                            {t('Click the button on top of this form to add the first medication to this prescription.')}
                        </Typography>
                    </Paper>
                )}
                {!isEmpty(medications) && (
                    <AgyleTable
                        columns={COLUMNS}
                        data={medications}
                        disableFilter
                        editItem={handleEditMedication}
                        deleteItem={handleDeleteMedication}
                        customActionItem={renderPreviewButton}
                        disabledAction={disabled}
                    />
                )}
            </AgyleAccordion>

            <MessageBox
                title={t('Confirm')}
                open={isConfirmOpen}
                haveAccept={{ yes: t('Yes'), no: t('No') }}
                warning
                onAccept={handleDeleteMedicineConfirmed}
                onClose={handleCloseConfirm}
                message={t('Are you sure you want to delete selected drug record?')}
            />
        </>
    );
};

export default PrescriptionsList;
